import React from 'react';
import * as R from 'ramda';
import { Box, Chip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { green, grey, orange, red } from '@material-ui/core/colors';
import moment from 'moment';

import {
  buildSubmissionQuestions,
  formatQuestionType,
  getOptionLabel,
  getOptionRationale,
  getQuestionOptions,
  isMultipleChoiceQuestion,
  isOptionCorrectAnswer,
  isOptionSelected,
} from 'resources/common/quizProgress/questionData';

const useStyles = makeStyles({
  root: {
    padding: '16px 24px 24px',
    backgroundColor: '#fafafa',
  },
  header: {
    marginBottom: 20,
    color: '#666',
    fontSize: '0.875rem',
  },
  questionCard: {
    border: `1px solid ${grey[300]}`,
    borderRadius: 8,
    padding: '20px 24px',
    marginBottom: 16,
    backgroundColor: '#fff',
  },
  questionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  questionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  questionTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#111',
    lineHeight: 1.4,
  },
  typeChip: {
    backgroundColor: grey[200],
    color: '#444',
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  scoreChip: {
    fontWeight: 700,
    height: 28,
    fontSize: '0.8125rem',
    color: '#fff',
    flexShrink: 0,
  },
  scoreHigh: {
    backgroundColor: green[600],
  },
  scoreMedium: {
    backgroundColor: orange[600],
  },
  scoreLow: {
    backgroundColor: red[600],
  },
  optionBlock: {
    marginBottom: 16,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 8,
  },
  indicator: {
    width: 18,
    height: 18,
    border: `2px solid ${grey[400]}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1,
    color: '#fff',
  },
  indicatorCircle: {
    borderRadius: '50%',
  },
  indicatorSquare: {
    borderRadius: 4,
  },
  indicatorCorrect: {
    borderColor: green[600],
    backgroundColor: green[600],
  },
  indicatorIncorrect: {
    borderColor: red[600],
    backgroundColor: red[600],
  },
  indicatorMissed: {
    borderColor: orange[700],
    backgroundColor: orange[700],
  },
  optionText: {
    fontSize: '0.9375rem',
    color: '#222',
    lineHeight: 1.4,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badgeCorrect: {
    backgroundColor: '#E8F5E9',
    color: green[800],
  },
  badgeIncorrect: {
    backgroundColor: '#FFEBEE',
    color: red[800],
  },
  badgeMissed: {
    backgroundColor: '#FFF3E0',
    color: orange[900],
  },
  rationaleBox: {
    marginTop: 4,
    padding: '10px 14px',
    borderRadius: 6,
    backgroundColor: '#FFF8E8',
    color: '#9A7B4F',
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  emptyState: {
    color: '#666',
    fontSize: '0.875rem',
  },
});

const DEFAULT_RATIONALE = 'No rationale authored for this option.';

const getSubmissionDate = (submission) =>
  submission.submittedAt || submission.created_at;

const getScoreChipClass = (score, classes) => {
  if (score >= 70) return `${classes.scoreChip} ${classes.scoreHigh}`;
  if (score >= 40) return `${classes.scoreChip} ${classes.scoreMedium}`;
  return `${classes.scoreChip} ${classes.scoreLow}`;
};

const getOptionTone = (isSelected, isCorrectAnswer) => {
  if (isSelected && isCorrectAnswer === true) return 'correct';
  if (isSelected && isCorrectAnswer === false) return 'incorrect';
  if (!isSelected && isCorrectAnswer === true) return 'missed';
  return 'neutral';
};

const OptionIndicator = ({ tone, multiple, classes }) => {
  const shapeClass = multiple
    ? classes.indicatorSquare
    : classes.indicatorCircle;
  const toneClass =
    tone === 'correct'
      ? classes.indicatorCorrect
      : tone === 'incorrect'
      ? classes.indicatorIncorrect
      : tone === 'missed'
      ? classes.indicatorMissed
      : '';

  const symbol =
    tone === 'incorrect' ? '✕' : tone === 'missed' ? '!' : '';

  return (
    <span className={`${classes.indicator} ${shapeClass} ${toneClass}`}>
      {symbol}
    </span>
  );
};

const OptionStatusBadge = ({ tone, classes }) => {
  if (tone === 'correct') {
    return (
      <span className={`${classes.statusBadge} ${classes.badgeCorrect}`}>
        Their answer — correct
      </span>
    );
  }
  if (tone === 'incorrect') {
    return (
      <span className={`${classes.statusBadge} ${classes.badgeIncorrect}`}>
        Their answer — incorrect
      </span>
    );
  }
  if (tone === 'missed') {
    return (
      <span className={`${classes.statusBadge} ${classes.badgeMissed}`}>
        Correct — they missed it
      </span>
    );
  }
  return null;
};

const QuestionDetail = ({ question }) => {
  const classes = useStyles();
  const options = getQuestionOptions(question);
  const multiple = isMultipleChoiceQuestion(question);
  const score =
    R.prop('score', question) ?? R.path(['answer', 'score'], question);
  const questionIndex = R.propOr(0, 'index', question);
  const questionType = formatQuestionType(question.type);

  return (
    <Box className={classes.questionCard}>
      <Box className={classes.questionHeader}>
        <Box className={classes.questionTitleRow}>
          <Typography className={classes.questionTitle} component="div">
            Q{questionIndex + 1}. {R.propOr('', 'title', question)}
          </Typography>
          {questionType && (
            <Chip
              size="small"
              label={questionType}
              className={classes.typeChip}
            />
          )}
        </Box>
        {!R.isNil(score) && (
          <Chip
            size="small"
            label={`${Math.round(score)}%`}
            className={getScoreChipClass(score, classes)}
          />
        )}
      </Box>

      {options.map((option, index) => {
        const isSelected = isOptionSelected(question, index);
        const isCorrectAnswer = isOptionCorrectAnswer(question, index);
        const tone = getOptionTone(isSelected, isCorrectAnswer);
        const rationale =
          getOptionRationale(question, option, index) || DEFAULT_RATIONALE;

        return (
          <Box key={index} className={classes.optionBlock}>
            <Box className={classes.optionRow}>
              <OptionIndicator
                tone={tone}
                multiple={multiple}
                classes={classes}
              />
              <Typography className={classes.optionText} component="span">
                {getOptionLabel(option, index)}
              </Typography>
              <OptionStatusBadge tone={tone} classes={classes} />
            </Box>
            <Box className={classes.rationaleBox}>{rationale}</Box>
          </Box>
        );
      })}
    </Box>
  );
};

const QuizSubmissionDetail = ({ record: submission, quiz }) => {
  const classes = useStyles();
  const questions = buildSubmissionQuestions(quiz, submission);
  const fullName = [submission.first_name, submission.last_name]
    .filter(Boolean)
    .join(' ');
  const submissionDate = getSubmissionDate(submission);
  const formattedDate = submissionDate
    ? moment(submissionDate).format('DD/MM/YYYY, h:mm A')
    : null;

  if (R.isEmpty(questions)) {
    const emptyMessage = submission.submissionId
      ? 'Question-level details are not available for this submission.'
      : 'No question-level submission data available.';

    return (
      <Box className={classes.root}>
        <Typography className={classes.emptyState}>{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.header}>
        {fullName}
        {R.isNil(submission.globalScore) ? '' : ` · ${submission.globalScore}%`}
        {formattedDate ? ` · submitted ${formattedDate}` : ''}
      </Typography>
      {questions.map((question) => (
        <QuestionDetail key={question.index} question={question} />
      ))}
    </Box>
  );
};

export default QuizSubmissionDetail;
